import type { SortField } from '@supply-mover-2-0/shared';

import type { FilterOption, FilterState } from '../filters/config/types';
import { useListStore } from '../stores';
import { ApiService } from './ApiService';

export type { SortField };

export interface AdjacentClientResponse {
	success: boolean;
	data?: {
		client: {
			_id: number;
			name?: string;
		};
		position: number;
		total: number;
		hasNext: boolean;
		hasPrev: boolean;
	};
	error?: string;
}

/**
 * Response from the navigation-window endpoint.
 */
export interface NavigationWindowResponse {
	success: boolean;
	data?: {
		prevIds: Array<number>;
		nextIds: Array<number>;
		total: number;
		currentPosition: number;
	};
	error?: string;
}

/**
 * Cached navigation window state.
 */
export interface NavigationWindow {
	prevIds: Array<number>;
	nextIds: Array<number>;
	currentId: number;
	total: number;
	currentPosition: number;
	timestamp: number;
}

export interface ListNavigationContext {
	listId?: string;
	listName?: string;
	sortFields: Array<SortField>;
	/**
	 * Backend-shaped filter values (transformFiltersForBackend output).
	 * Consumed by the detail-page prev/next navigation, which feeds them
	 * straight into list queries. NOT applicable as UI filter state.
	 */
	filters: Record<string, unknown>;
	/**
	 * Raw UI filter state (FilterState: filterType -> Array<FilterOption>).
	 * Consumed by surfaces that rebuild filter CONTROLS from the context —
	 * the client map view. Kept separate from `filters` because the two
	 * shapes are incompatible: applying backend-shaped values as UI state
	 * produced malformed queries and broken chips on the map (PD-13205).
	 * Optional: contexts written before this field existed lack it.
	 */
	filterState?: FilterState;
	filterSummary?: string;
	sourceUrl: string;
	timestamp: number;
	/**
	 * IDs of the detail records the user has actually visited within this
	 * list-browsing session (the row they clicked plus every prev/next hop).
	 * Lets the prev/next controls reappear when the user leaves a detail page
	 * (e.g. into a related record) and returns to one they were paging through,
	 * without relying on the volatile `fromList` location state which React
	 * Router drops on any plain link navigation.
	 */
	browsedIds?: Array<number>;
}

const WINDOW_SIZE = 20; // Number of IDs to fetch in each direction
const EDGE_THRESHOLD = 10; // Refresh window when within this many IDs of edge

/**
 * API path mapping for each entity type.
 * Add new entities here as navigation is implemented.
 */
const ENTITY_API_PATHS: Record<string, string> = {
	client: '/clients',
	item: '/items',
	order: '/orders',
	quote: '/quotes',
	invoice: '/invoices',
	vendor: '/vendors',
	competitor: '/competitors',
	job: '/jobs',
	opportunity: '/opportunities',
	purchase_order: '/purchase-orders',
	price_contract: '/price-contracts',
	location: '/locations',
	warehouse: '/warehouses',
	issue: '/issues',
	license: '/licenses',
	task: '/tasks',
	// Post-Phase-A: the `trip` listKey resolves to the trip detail page,
	// and the `route` listKey resolves to the recurring-route detail page.
	// Both live under /distribution/... — the legacy /routes/... namespace
	// is gone.
	trip: '/distribution/trips',
	route: '/distribution',
	driver: '/distribution/drivers',
	vehicle: '/distribution/vehicles',
	conversation_session: '/ai-agents/sessions',
};

/**
 * Service for list navigation operations.
 * Handles API calls for adjacent records and context storage.
 *
 * Uses a sliding window approach for efficient navigation:
 * - Fetches 20 prev + current + 20 next IDs in one API call
 * - Caches the window in memory
 * - Navigates using cached IDs
 * - Refreshes window when approaching the edge (within 10 IDs)
 */
class ListNavigationServiceClass {
	// In-memory window cache per listKey
	private readonly windowCache: Map<string, NavigationWindow> = new Map();

	/**
	 * Get the navigation window for an entity.
	 * Uses cache if available and current ID is within the window.
	 */
	async getNavigationWindow(
		listKey: string,
		currentId: number,
		sortFields: Array<SortField>,
		filters: Record<string, unknown>,
		options: { forceRefresh?: boolean; listId?: string } = {},
	): Promise<NavigationWindow | null> {
		const cacheKey = this.getCacheKey(listKey, filters, sortFields);
		const cached = this.windowCache.get(cacheKey);

		// Check if we can use cached window
		if (cached != null && !options.forceRefresh) {
			if (this.isInWindow(cached, currentId)) {
				// Update position based on where currentId is in the window
				const updatedWindow = this.recalculatePosition(cached, currentId);

				if (updatedWindow != null) {
					// Check if we need to refresh (approaching edge)
					if (!this.needsRefresh(updatedWindow)) {
						return updatedWindow;
					}
				}
			}
		}

		// Fetch fresh window from API
		const window = await this.fetchNavigationWindow(
			listKey,
			currentId,
			sortFields,
			filters,
			options.listId,
		);

		if (window != null) {
			this.windowCache.set(cacheKey, window);
		}

		return window;
	}

	/**
	 * Navigate to the next or previous record using cached window.
	 * Returns the ID to navigate to, or null if at the edge.
	 */
	getAdjacentId(
		listKey: string,
		currentId: number,
		direction: 'next' | 'prev',
		sortFields: Array<SortField>,
		filters: Record<string, unknown>,
	): number | null {
		const cacheKey = this.getCacheKey(listKey, filters, sortFields);
		const cached = this.windowCache.get(cacheKey);

		if (cached == null) {
			return null;
		}

		// Find current position in window
		// Both prevIds and nextIds are in ascending order, combine directly
		const allIds = [...cached.prevIds, cached.currentId, ...cached.nextIds];
		const currentIndex = allIds.indexOf(currentId);

		if (currentIndex === -1) {
			return null;
		}

		if (direction === 'next') {
			return currentIndex < allIds.length - 1
				? (allIds[currentIndex + 1] ?? null)
				: null;
		}

		return currentIndex > 0 ? (allIds[currentIndex - 1] ?? null) : null;
	}

	/**
	 * Check if we have cached navigation data for an entity.
	 */
	hasNavigationWindow(
		listKey: string,
		sortFields: Array<SortField>,
		filters: Record<string, unknown>,
	): boolean {
		const cacheKey = this.getCacheKey(listKey, filters, sortFields);

		return this.windowCache.has(cacheKey);
	}

	/**
	 * Clear navigation window cache for an entity type.
	 */
	clearNavigationWindow(listKey: string): void {
		// Clear all entries that start with this listKey
		for (const key of this.windowCache.keys()) {
			if (key.startsWith(`${listKey}:`)) {
				this.windowCache.delete(key);
			}
		}
	}

	/**
	 * Clear all navigation window caches.
	 */
	clearAllNavigationWindows(): void {
		this.windowCache.clear();
	}

	/**
	 * Fetch navigation window from API.
	 */
	private async fetchNavigationWindow(
		listKey: string,
		currentId: number,
		sortFields: Array<SortField>,
		filters: Record<string, unknown>,
		listId?: string,
	): Promise<NavigationWindow | null> {
		const basePath = ENTITY_API_PATHS[listKey];

		if (basePath == null) {
			console.warn(`No API path defined for listKey: ${listKey}`);

			return null;
		}

		const params = new URLSearchParams();
		params.set('windowSize', String(WINDOW_SIZE));
		params.set('sortFields', JSON.stringify(sortFields));

		if (Object.keys(filters).length > 0) {
			params.set('filters', JSON.stringify(filters));
		}

		if (listId != null) {
			params.set('listId', listId);
		}

		const url = `${basePath}/${currentId}/navigation-window?${params.toString()}`;

		try {
			const response = await ApiService.apiGet<NavigationWindowResponse>(url);

			if (!response.success || response.data == null) {
				return null;
			}

			return {
				prevIds: response.data.prevIds,
				nextIds: response.data.nextIds,
				currentId,
				total: response.data.total,
				currentPosition: response.data.currentPosition,
				timestamp: Date.now(),
			};
		} catch (error) {
			console.error('Error fetching navigation window:', error);

			return null;
		}
	}

	/**
	 * Generate cache key for window storage.
	 */
	private getCacheKey(
		listKey: string,
		filters: Record<string, unknown>,
		sortFields: Array<SortField>,
	): string {
		return `${listKey}:${JSON.stringify(sortFields)}:${JSON.stringify(filters)}`;
	}

	/**
	 * Check if an ID is within the cached window.
	 */
	private isInWindow(window: NavigationWindow, id: number): boolean {
		if (id === window.currentId) {
			return true;
		}

		if (window.prevIds.includes(id) || window.nextIds.includes(id)) {
			return true;
		}

		return false;
	}

	/**
	 * Recalculate position when navigating within cached window.
	 */
	private recalculatePosition(
		window: NavigationWindow,
		currentId: number,
	): NavigationWindow | null {
		if (currentId === window.currentId) {
			return window;
		}

		// Find in prev or next arrays
		const prevIndex = window.prevIds.indexOf(currentId);

		if (prevIndex !== -1) {
			// Moved backward - recalculate
			// prevIds is in ascending order [10, 11, ..., 29], so prevIndex tells us position from start
			const newPosition =
				window.currentPosition - (window.prevIds.length - prevIndex);
			const newPrevIds = window.prevIds.slice(0, prevIndex);
			const newNextIds = [
				...window.prevIds.slice(prevIndex + 1),
				window.currentId,
				...window.nextIds,
			];

			return {
				...window,
				currentId,
				currentPosition: newPosition,
				prevIds: newPrevIds,
				nextIds: newNextIds,
			};
		}

		const nextIndex = window.nextIds.indexOf(currentId);

		if (nextIndex !== -1) {
			// Moved forward - recalculate
			const newPosition = window.currentPosition + nextIndex + 1;
			const newPrevIds = [
				...window.prevIds,
				window.currentId,
				...window.nextIds.slice(0, nextIndex),
			];
			const newNextIds = window.nextIds.slice(nextIndex + 1);

			return {
				...window,
				currentId,
				currentPosition: newPosition,
				prevIds: newPrevIds,
				nextIds: newNextIds,
			};
		}

		return null;
	}

	/**
	 * Check if window needs to be refreshed (approaching edge).
	 */
	private needsRefresh(window: NavigationWindow): boolean {
		return (
			window.prevIds.length < EDGE_THRESHOLD ||
			window.nextIds.length < EDGE_THRESHOLD
		);
	}

	/**
	 * Save navigation context to list store.
	 * Called when navigating from a list to a detail page.
	 */
	saveListContext(listKey: string, context: ListNavigationContext): void {
		try {
			useListStore.getState().setNavigationContext(listKey, context);
		} catch (error) {
			console.warn('Failed to save list context:', error);
		}
	}

	/**
	 * Load navigation context from list store.
	 * Returns null if context doesn't exist or is expired.
	 */
	loadListContext(listKey: string): ListNavigationContext | null {
		try {
			// getNavigationContext already handles TTL checking.
			// The store's ListNavigationContext types sortFields loosely
			// (direction: string); contexts only ever enter the store via
			// saveListContext, which takes this file's stricter shape, so
			// narrowing on the way back out is safe.
			return useListStore
				.getState()
				.getNavigationContext(listKey) as ListNavigationContext | null;
		} catch (error) {
			console.warn('Failed to load list context:', error);

			return null;
		}
	}

	/**
	 * Extract the raw UI filter state carried by a navigation context.
	 *
	 * Returns `context.filterState` only when every entry has the FilterState
	 * shape (an array of FilterOption-like objects with `id` + `label`);
	 * otherwise null. Contexts written by older sessions carry only the
	 * backend-shaped `filters` field, whose values (raw ids, range objects)
	 * corrupt UI state if applied directly — callers get null and fall back
	 * to the list's defaults instead (PD-13205). All-or-nothing on purpose:
	 * a context is written atomically by one writer, so a partially-valid
	 * state means corruption, and applying half of it would misrepresent
	 * what the user had selected.
	 */
	public getContextFilterState(
		context: ListNavigationContext | null,
	): FilterState | null {
		const candidate = context?.filterState;

		if (candidate == null || typeof candidate !== 'object') {
			return null;
		}

		const isFilterOption = (value: unknown): value is FilterOption => {
			if (value == null || typeof value !== 'object') {
				return false;
			}

			const option = value as Partial<FilterOption>;

			return (
				(typeof option.id === 'string' || typeof option.id === 'number') &&
				typeof option.label === 'string'
			);
		};

		for (const values of Object.values(candidate)) {
			if (!Array.isArray(values) || !values.every(isFilterOption)) {
				return null;
			}
		}

		return candidate;
	}

	/**
	 * Record that the user has viewed the detail page for `id` within the
	 * current list-browsing session. Appended (de-duplicated) to the saved
	 * context's `browsedIds` so the prev/next controls can reappear when the
	 * user returns to a record they were paging through.
	 *
	 * No-ops when there is no context (or it has expired) — we never resurrect
	 * an expired session. Preserves the existing `timestamp` so the 30-minute
	 * TTL still counts from when the user entered the list, rather than sliding
	 * forward on every visit.
	 */
	public recordBrowsedId(listKey: string, id: number): void {
		const context = this.loadListContext(listKey);

		if (context == null) {
			return;
		}

		const browsedIds = context.browsedIds ?? [];

		if (browsedIds.includes(id)) {
			return;
		}

		this.saveListContext(listKey, {
			...context,
			browsedIds: [...browsedIds, id],
		});
	}

	/**
	 * Clear navigation context from list store.
	 */
	clearListContext(listKey: string): void {
		try {
			useListStore.getState().clearNavigationContext(listKey);
		} catch (error) {
			console.warn('Failed to clear list context:', error);
		}
	}

	/**
	 * Build a human-readable summary of active filters.
	 */
	buildFilterSummary(filters: Record<string, unknown>): string {
		const summaryParts: Array<string> = [];

		// Common filter display names
		const filterLabels: Record<string, string> = {
			leadOrCustomer: 'Type',
			salesStatus: 'Sales Status',
			businessTypes: 'Business Type',
			salespersonIds: 'Sales Rep',
			groupIds: 'Group',
			geography: 'Geography',
			specialFilters: 'Special',
		};

		for (const [key, value] of Object.entries(filters)) {
			if (value == null || (Array.isArray(value) && value.length === 0)) {
				continue;
			}

			const label = filterLabels[key] ?? key;

			if (Array.isArray(value)) {
				if (value.length === 1) {
					summaryParts.push(`${label}: ${value[0]}`);
				} else {
					summaryParts.push(`${label}: ${value.length} selected`);
				}
			} else if (typeof value === 'object') {
				// Date range or other object
				summaryParts.push(`${label}: set`);
			} else {
				summaryParts.push(`${label}: ${value}`);
			}
		}

		return summaryParts.join(', ');
	}

	/**
	 * Check if navigation state indicates a "return" navigation (from detail page back to list).
	 * Used to determine if sticky filters should be restored.
	 */
	public isReturnNavigation(locationState: unknown): boolean {
		const state = locationState as {
			fromList?: boolean;
			returning?: boolean;
		} | null;

		return state?.fromList === true || state?.returning === true;
	}

	/**
	 * Check if current URL matches the stored sourceUrl (indicating a page reload).
	 * Used to determine if sticky filters should be restored on refresh.
	 */
	public isPageReload(listKey: string, currentPath: string): boolean {
		const context = this.loadListContext(listKey);

		if (context == null) {
			return false;
		}

		// If stored sourceUrl matches current path, user likely refreshed the page
		return context.sourceUrl === currentPath;
	}

	/**
	 * Clear all list navigation contexts.
	 * Called when user explicitly navigates away via main navigation.
	 * This ensures fresh state when navigating to a section via main nav,
	 * while preserving context for browser back/page reload.
	 */
	public clearAllListContexts(): void {
		try {
			useListStore.getState().clearAllNavigationContexts();
		} catch (error) {
			console.warn('Failed to clear list contexts:', error);
		}
	}
}

export const ListNavigationService = new ListNavigationServiceClass();
